import { Controller } from "@hotwired/stimulus"

const ACTIVE_TAB   = ["text-gray-600", "border-[#ddd]", "border-b-transparent", "bg-white", "cursor-default"]
const INACTIVE_TAB = ["text-[#2f4e72]", "border-transparent", "hover:border-t-[#eee]", "hover:border-x-[#eee]", "hover:border-b-[#ddd]", "hover:bg-[#eee]"]

export default class extends Controller {
  static targets = ["logRow", "logDetail", "logMeta", "panel", "panelCard", "backdrop", "panelContent", "placeholder", "modalTitle", "prevBtn", "nextBtn", "requestTab", "responseTab"]

  connect() {
    this.currentIndex = 0
    this.activeTab    = "request"
    this.boundKeydown = this.handleKeydown.bind(this)
    this.resizeTimer  = null
    this.boundResize  = this.handleResize.bind(this)
    window.addEventListener("resize", this.boundResize)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
    window.removeEventListener("resize", this.boundResize)
    document.body.classList.remove("overflow-hidden")
  }

  handleResize() {
    this.panelCardTarget.style.transition = "none"
    clearTimeout(this.resizeTimer)
    this.resizeTimer = setTimeout(() => {
      this.panelCardTarget.style.transition = ""
    }, 100)
  }

  selectLog(event) {
    this.currentIndex = parseInt(event.currentTarget.dataset.index)
    this.highlightLogRow(this.currentIndex)
    this.openPanel()
  }

  openPanel() {
    this.showLogDetail(this.currentIndex)
    this.updateModalHeader()
    this.setActiveTab("request")

    // Mobile: enable interactions, fade in backdrop, slide in card
    this.panelTarget.classList.remove("max-lg:pointer-events-none")
    this.backdropTarget.classList.remove("opacity-0")
    this.panelCardTarget.classList.remove("max-lg:translate-x-full")

    // Show content, hide placeholder
    this.placeholderTarget.classList.add("hidden")
    this.panelContentTarget.classList.remove("hidden")
    this.panelContentTarget.classList.add("flex")

    if (window.innerWidth < 1024) document.body.classList.add("overflow-hidden")

    // Deduplicate keydown listener
    document.removeEventListener("keydown", this.boundKeydown)
    document.addEventListener("keydown", this.boundKeydown)
  }

  closePanel() {
    // Mobile: slide out card, fade out backdrop, disable interactions
    this.panelCardTarget.classList.add("max-lg:translate-x-full")
    this.backdropTarget.classList.add("opacity-0")
    this.panelTarget.classList.add("max-lg:pointer-events-none")

    // Hide content, restore placeholder
    this.panelContentTarget.classList.remove("flex")
    this.panelContentTarget.classList.add("hidden")
    this.placeholderTarget.classList.remove("hidden")

    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this.boundKeydown)
    this.logRowTargets.forEach(row => row.classList.remove("bg-blue-50"))
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--
      this.highlightLogRow(this.currentIndex)
      this.showLogDetail(this.currentIndex)
      this.updateModalHeader()
    }
  }

  next() {
    if (this.currentIndex < this.logDetailTargets.length - 1) {
      this.currentIndex++
      this.highlightLogRow(this.currentIndex)
      this.showLogDetail(this.currentIndex)
      this.updateModalHeader()
    }
  }

  switchTab(event) {
    this.setActiveTab(event.currentTarget.dataset.tab)
  }

  setActiveTab(tab) {
    this.activeTab = tab
    const isRequest = tab === "request"

    ACTIVE_TAB.forEach(c => this.requestTabTarget.classList.toggle(c, isRequest))
    INACTIVE_TAB.forEach(c => this.requestTabTarget.classList.toggle(c, !isRequest))
    ACTIVE_TAB.forEach(c => this.responseTabTarget.classList.toggle(c, !isRequest))
    INACTIVE_TAB.forEach(c => this.responseTabTarget.classList.toggle(c, isRequest))

    const detail = this.logDetailTargets[this.currentIndex]
    if (detail) {
      detail.querySelector('[data-section="request"]').classList.toggle("hidden", !isRequest)
      detail.querySelector('[data-section="response"]').classList.toggle("hidden", isRequest)
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.closePanel()
    if (event.key === "ArrowLeft"  || event.key === "ArrowUp")   { event.preventDefault(); this.prev() }
    if (event.key === "ArrowRight" || event.key === "ArrowDown") { event.preventDefault(); this.next() }
  }

  showLogDetail(index) {
    this.logDetailTargets.forEach((detail, i) => {
      detail.classList.toggle("hidden", i !== index)
    })
    this.logMetaTargets.forEach((meta, i) => {
      const visible = i === index
      meta.classList.toggle("hidden", !visible)
      if (visible) meta.classList.add("flex")
    })
    const detail = this.logDetailTargets[index]
    if (detail) {
      const isRequest = this.activeTab === "request"
      detail.querySelector('[data-section="request"]').classList.toggle("hidden", !isRequest)
      detail.querySelector('[data-section="response"]').classList.toggle("hidden", isRequest)
    }
  }

  updateModalHeader() {
    const row = this.logRowTargets[this.currentIndex]
    if (row) {
      const status = parseInt(row.dataset.status)
      const badgeClass = status >= 400
        ? "bg-red-100 text-red-700"
        : "bg-green-100 text-green-700"
      this.modalTitleTarget.innerHTML = `
        <span class="inline-flex items-center gap-2">
          <span class="${badgeClass} text-xs font-semibold px-2 py-1 rounded">${status}</span>
          ${row.dataset.method} ${row.dataset.path}
        </span>`
    }
    const total = this.logDetailTargets.length
    this.prevBtnTarget.disabled = this.currentIndex === 0
    this.nextBtnTarget.disabled = this.currentIndex === total - 1
  }

  highlightLogRow(index) {
    this.logRowTargets.forEach((row, i) => {
      row.classList.toggle("bg-blue-50", i === index)
    })
  }
}
